import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, NavigateFunction } from "react-router-dom";
import { useOktaAuth } from "@okta/okta-react";
import { triggerOktaLogin } from "../../utils/oktaUtil";
import { navigateTo } from "../../../navigateTo";
import {IRIS_ORIGINAL_URI_KEY} from "../root/SecureRoute";
import {LoginContent, RegionHero} from "../../model/RegionLoginContent.model";
import {LoginContentApi} from "../../api/LoginContentApi";
import {LoadingSpinner} from "../icons/LoadingSpinner.component";

export const LoginLandingPage: React.FunctionComponent = () => {
    const { authState } = useOktaAuth();
    const navigate: NavigateFunction = useNavigate();
    const [searchParams] = useSearchParams();
    const [landingContent, setLandingContent] = useState<LoginContent | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (authState?.isAuthenticated) {
            navigateTo("/home", navigate);
        }
    }, [authState?.isAuthenticated, navigate]);

    useEffect(() => {
        const testCountry = searchParams.get("testCountry") ?? undefined;
        const testLang = searchParams.get("testLang") ?? undefined;
        setIsLoading(true);
        LoginContentApi.getPublishedLoginContent(testCountry, testLang)
            .then(setLandingContent)
            .catch(() => setLandingContent(null))
            .finally(() => setIsLoading(false));
    }, [searchParams]);

    const handleSignIn = () => {
        const storedUri = localStorage.getItem(IRIS_ORIGINAL_URI_KEY);

        let originalUri = storedUri || "/home";
        if (originalUri === "/landing" || originalUri === "/" || originalUri === "/login/callback") {
            originalUri = "/home";
        }

        triggerOktaLogin(originalUri).catch((err) =>
            console.error("Sign-in error:", err)
        );
    };

    const renderAonLogo = () => (
        <div role="img" aria-label="Aon" className="landing-page__aon-logo" />
    );

    const renderPublishedContent = () => {
        if (isLoading) {
            return <LoadingSpinner />;
        }

        if (!landingContent) {
            return null;
        }

        const { hero, stats, servicePills } = landingContent;

        const renderHeroContent = (heroContent: RegionHero) => {
            return <div className="landing-page__hero" data-testid="landing-hero">
                    <p>{heroContent.tagline}</p>
                    <h2>{heroContent.headline}</h2>
                    <p>{heroContent.description}</p>
                </div>;
        };

        const renderStats = () => {
            return <div style={{ paddingTop: "40px" }} data-testid="landing-stats">
                    {stats.map((stat, index) => (
                        <div key={index} className="landing-page__stat">
                            <span><b>Value:</b> {stat.value}</span>
                            <span style={{ marginLeft: "30px" }}><b>Label:</b> {stat.label}</span>
                        </div>
                    ))}
                </div>;
        };

        const renderServicePills = () => {
          return <div data-testid="landing-service-pills" style={{ paddingTop: "40px" }}>
                  {servicePills.map((pill, index) => (
                      <div key={index} className="landing-page__service-pill-container">
                          <span className="landing-page__service-pill"><b>Name:</b> {pill.label}</span>
                          <span style={{ marginLeft: "30px" }}><b>Url:</b> {pill.url}</span>
                      </div>
                  ))}
              </div>;
        };

        return (
            <div className="landing-page__published-content" data-testid="landing-published-content">
                {hero && renderHeroContent(hero)}
                {stats.length > 0 && renderStats()}
                {servicePills.length > 0 && renderServicePills()}
            </div>
        );
    };

    if (authState?.isAuthenticated) {
        return null;
    }

    return (
        <div className="landing-page">
            <div className="landing-page__left-panel">
                <div className="landing-page__logo-header">
                    <span className="landing-page__logo-text">AON</span>
                    <span className="landing-page__logo-divider">|</span>
                    <span className="landing-page__logo-iris">Iris</span>
                </div>
                {renderPublishedContent()}
            </div>

            <div className="landing-page__right-panel">
                <div className="landing-page__sign-in-card">
                    <h1 className="landing-page__welcome-title">Welcome Back</h1>
                    <p className="landing-page__welcome-subtitle">Sign in to access your investment insights</p>

                    <div className="landing-page__sign-in-box">
                        {renderAonLogo()}
                        <h2 className="landing-page__sign-in-title">Sign In</h2>
                        <p className="landing-page__sign-in-description">
                            Click below to securely sign in with your Aon credentials
                        </p>

                        <button
                            className="landing-page__sign-in-button"
                            onClick={handleSignIn}
                            data-testid="landing-sign-in-button"
                        >
                            <i className="fa-solid fa-arrow-right-to-bracket" aria-hidden="true" />
                            Continue to Sign In
                        </button>

                        <div className="landing-page__support-link">
                            Need help?{" "}
                            {/* eslint-disable-next-line react/forbid-dom-props */}
                            <a href="mailto:support@aoniris.com">Contact Support</a>
                        </div>
                    </div>

                    <div className="landing-page__secure-notice">
                        <h3 className="landing-page__secure-notice-title">Secure Authentication</h3>
                        <p className="landing-page__secure-notice-text">
                            You'll be redirected to Aon's secure login page to enter your credentials,
                            then returned to Iris.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
